import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { useQuote } from "@/context/QuoteContext";
import { CONTACT } from "@/lib/api";
import { Button } from "@/components/ui/button";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/products", label: "Products" },
  { to: "/bulk-orders", label: "Bulk Orders" },
  { to: "/wholesale-pricing", label: "Wholesale" },
  { to: "/become-distributor", label: "Distributor" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openQuote } = useQuote();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-cream/90 backdrop-blur-md shadow-[0_4px_24px_rgba(15,81,50,0.08)]" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="max-w-7xl mx-auto px-5 lg:px-8 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="navbar-logo">
          <img src="/brand/logo-transparent.png" alt="Leaf Alchemy" className="h-14 w-auto object-contain" />
        </Link>

        <div className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors hover:text-gold relative py-1 ${
                  isActive ? "text-gold" : scrolled ? "text-forest" : "text-forest"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <a href={`tel:${CONTACT.phoneRaw}`} className="text-forest hover:text-gold transition-colors" data-testid="navbar-call">
            <Phone size={18} />
          </a>
          <Button
            onClick={() => openQuote()}
            data-testid="navbar-quote-btn"
            className="rounded-full bg-gold hover:bg-forest text-ink hover:text-cream font-medium transition-colors"
          >
            Get Wholesale Quote
          </Button>
        </div>

        <button
          className="lg:hidden text-forest"
          onClick={() => setOpen(!open)}
          data-testid="navbar-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden bg-cream overflow-hidden border-t border-forest/10"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className="py-2.5 text-forest font-medium border-b border-forest/5"
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </NavLink>
              ))}
              <Button onClick={() => openQuote()} className="mt-3 rounded-full bg-gold text-ink" data-testid="mobile-quote-btn">
                Get Wholesale Quote
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
